import { contactAuthUrl } from './apiBase'
import { decodeJwtPayload } from './experience'

export type ContactAuthPhase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | {
      kind: 'ready'
      smartAuth: string
      jwtPayload: Record<string, unknown> | null
      jwtPreview: string
    }

export type ContactAuthResult = Exclude<ContactAuthPhase, { kind: 'loading' }>

function readSmartAuth(data: unknown): string | null {
  if (!isRecord(data)) return null
  const sa = data.smart_auth
  return typeof sa === 'string' ? sa : null
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

const inflightAuth = new Map<string, Promise<ContactAuthResult>>()

async function fetchContactAuthOnce(token: string): Promise<ContactAuthResult> {
  try {
    const correlationId = crypto.randomUUID()
    const res = await fetch(contactAuthUrl(token), {
      headers: { correlationid: correlationId },
    })
    if (!res.ok) {
      const body = await res.text()
      return {
        kind: 'error',
        message: `${res.status} ${body.slice(0, 200)}`,
        status: res.status,
      }
    }
    const data: unknown = await res.json()
    const smartAuth = readSmartAuth(data)
    if (!smartAuth) {
      return { kind: 'error', message: 'Invalid auth response' }
    }
    const jwtPayload = decodeJwtPayload(smartAuth)
    const preview = jwtPayload ? JSON.stringify(jwtPayload, null, 2) : '{}'
    return { kind: 'ready', smartAuth, jwtPayload, jwtPreview: preview }
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    return { kind: 'error', message }
  }
}

export function fetchContactAuth(token: string): Promise<ContactAuthResult> {
  const key = token.trim()
  const existing = inflightAuth.get(key)
  if (existing) return existing
  const promise = fetchContactAuthOnce(key)
  inflightAuth.set(key, promise)
  void promise.finally(() => {
    inflightAuth.delete(key)
  })
  return promise
}
